import { createTokens } from "@sail/engine";
const family = createTokens({
    path: "font.family",
    schema: {}
}, {
    typeface: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
    unitsPerEm: 2048,
    ascender: 1980,
    capHeight: 1443,
    xHeight: 1078,
    descender: -432,
    lineGap: 0
});
const monospaceFamily = createTokens({
    path: "font.monospace.family",
    schema: {}
}, {
    // NOTE(koop): We may want to consider updating this font stack to account
    // for font availability and other operating systems. Source Code Pro isn't
    // installed on many machines, and Menlo and Monaco are typically only
    // installed on macOS (to my knowledge).
    typeface: `'Source Code Pro', Menlo, Monaco, monospace`,
    // Based on installation rates above, the following metrics are for Menlo:
    unitsPerEm: 2048,
    ascender: 1556,
    capHeight: 1493,
    xHeight: 1120,
    descender: -492,
    lineGap: 410
});
const weight = createTokens("font.weight", {
    light: "300",
    normal: "400",
    semibold: "600",
    bold: "700"
});
const labelFamily = createTokens("font.label.family", family);
const bodyFamily = createTokens("font.body.family", family);
const headingFamily = createTokens("font.heading.family", family);
const displayFamily = createTokens("font.display.family", family);
const displayXlarge = createTokens("font.display.xlarge", {
    family: displayFamily,
    size: "56px",
    lineHeight: "64px",
    weight: weight.bold
});
const displayLarge = createTokens("font.display.large", {
    family: displayFamily,
    size: "48px",
    lineHeight: "56px",
    weight: weight.bold
});
const displayMedium = createTokens("font.display.medium", {
    family: displayFamily,
    size: "40px",
    lineHeight: "48px",
    weight: weight.bold
});
const displaySmall = createTokens("font.display.small", {
    family: displayFamily,
    size: "32px",
    lineHeight: "40px",
    weight: weight.bold
});
const headingXlarge = createTokens("font.heading.xlarge", {
    family: headingFamily,
    size: "28px",
    lineHeight: "36px",
    weight: weight.bold,
    transform: "none"
});
const headingLarge = createTokens("font.heading.large", {
    family: headingFamily,
    size: "24px",
    lineHeight: "32px",
    weight: weight.bold,
    transform: "none"
});
const headingMedium = createTokens("font.heading.medium", {
    family: headingFamily,
    size: "20px",
    lineHeight: "28px",
    weight: weight.bold,
    transform: "none"
});
const headingSmall = createTokens("font.heading.small", {
    family: headingFamily,
    size: "16px",
    lineHeight: "24px",
    weight: weight.bold,
    transform: "none"
});
const headingXsmall = createTokens("font.heading.xsmall", {
    family: headingFamily,
    size: "12px",
    lineHeight: "20px",
    weight: weight.bold,
    transform: "none"
});
const bodyLarge = createTokens("font.body.large", {
    family: bodyFamily,
    size: "18px",
    lineHeight: "28px",
    weight: weight.normal,
    transform: "none"
});
const bodyMedium = createTokens("font.body.medium", {
    family: bodyFamily,
    size: "16px",
    lineHeight: "24px",
    weight: weight.normal,
    transform: "none"
});
const bodySmall = createTokens("font.body.small", {
    family: bodyFamily,
    size: "14px",
    lineHeight: "20px",
    weight: weight.normal,
    transform: "none"
});
const labelLarge = createTokens("font.label.large", {
    family: labelFamily,
    size: "16px",
    lineHeight: "24px",
    weight: weight.normal,
    transform: "none"
});
const labelMedium = createTokens("font.label.medium", {
    family: labelFamily,
    size: "14px",
    lineHeight: "20px",
    weight: weight.normal,
    transform: "none"
});
const labelSmall = createTokens("font.label.small", {
    family: labelFamily,
    size: "12px",
    lineHeight: "16px",
    weight: weight.normal,
    transform: "none"
});
export const font = createTokens("font", {
    family,
    weight,
    monospace: {
        family: monospaceFamily
    },
    display: {
        family: displayFamily,
        xlarge: {
            $default: displayXlarge,
            subdued: {
                $default: displayXlarge,
                weight: weight.normal
            }
        },
        large: {
            $default: displayLarge,
            subdued: {
                $default: displayLarge,
                weight: weight.normal
            }
        },
        medium: {
            $default: displayMedium,
            subdued: {
                $default: displayMedium,
                weight: weight.normal
            }
        },
        small: {
            $default: displaySmall,
            subdued: {
                $default: displaySmall,
                weight: weight.normal
            }
        }
    },
    heading: {
        family: headingFamily,
        xlarge: {
            $default: headingXlarge,
            subdued: {
                $default: headingXlarge,
                weight: weight.normal
            }
        },
        large: {
            $default: headingLarge,
            subdued: {
                $default: headingLarge,
                weight: weight.normal
            }
        },
        medium: {
            $default: headingMedium,
            subdued: {
                $default: headingMedium,
                weight: weight.normal
            }
        },
        small: {
            $default: headingSmall,
            subdued: {
                $default: headingSmall,
                weight: weight.normal
            }
        },
        xsmall: {
            $default: headingXsmall,
            subdued: {
                $default: headingXsmall,
                weight: weight.normal
            }
        }
    },
    body: {
        family: bodyFamily,
        large: {
            $default: bodyLarge,
            emphasized: {
                $default: bodyLarge,
                weight: weight.semibold
            }
        },
        medium: {
            $default: bodyMedium,
            emphasized: {
                $default: bodyMedium,
                weight: weight.semibold
            }
        },
        small: {
            $default: bodySmall,
            emphasized: {
                $default: bodySmall,
                weight: weight.semibold
            }
        }
    },
    label: {
        family: labelFamily,
        large: {
            $default: labelLarge,
            emphasized: {
                $default: labelLarge,
                weight: weight.semibold
            }
        },
        medium: {
            $default: labelMedium,
            emphasized: {
                $default: labelMedium,
                weight: weight.semibold
            }
        },
        small: {
            $default: labelSmall,
            emphasized: {
                $default: labelSmall,
                weight: weight.semibold
            }
        }
    }
});

//# sourceMappingURL=index.js.map